import React from "react";
import { CheckCircle2, File as FileIcon, Plus, Trash2 } from "lucide-react";
import { WarningModal } from "../../../../components/WarningModal/WarningModal";
import { Input, Select } from "../../../../components/Core/Form/component";
import FileUploader from "../../../../components/FileUploader/FileUploader";
import { CompleteSalePayment } from "../interfaces";
import { SalesDetail } from "../../../../interfaces/sales";

interface CompleteSaleModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payments: CompleteSalePayment[]) => Promise<void>;
  isLoading: boolean;
  sale: SalesDetail;
  /** YOSHI-10935: SPEI is how external orders are collected, so only they can be paid with it */
  allowSpei: boolean;
}

type PaymentType = CompleteSalePayment["paymentType"];

const PAYMENT_TYPE_OPTIONS: { name: string; path: PaymentType }[] = [
  { name: "Tarjeta", path: "card" },
  { name: "Efectivo", path: "cash" },
  // YOSHI-10935: a bank transfer a person already checked arrived; it is recorded, not processed
  { name: "SPEI", path: "spei" },
];

// Same rule the API applies: Banxico tracking keys are 1 to 30 letters or digits
const SPEI_TRACKING_KEY_REGEX: RegExp = /^[A-Za-z0-9]{1,30}$/;
const SPEI_TEXT_MAX_LENGTH: number = 100;
const SPEI_RECEIPT_MAX_SIZE: number = 1024 * 1024;

const MERCHANT_OPTIONS = [
  { name: "Santander", path: "santander" },
  { name: "NetPay", path: "netpay" },
  { name: "Clover", path: "clover" },
  { name: "Mercado Pago", path: "mercadopago" },
];

const MAX_PAYMENTS = 5;

const ACTIVE_PAYMENT_STATUSES = new Set<number>([1, 2, 8]);

interface PaymentRow {
  subtotal: string;
  tip: string;
  paymentType: PaymentType;
  merchant: string;
  trackingKey: string;
  /** Picker value (local yyyy-MM-ddTHH:mm) */
  operationAt: string;
  originBank: string;
  senderName: string;
  reference: string;
  receiptFile: File | null;
}

const buildEmptyRow = (): PaymentRow => {
  return {
    subtotal: "",
    tip: "",
    paymentType: "card",
    merchant: "",
    trackingKey: "",
    operationAt: "",
    originBank: "",
    senderName: "",
    reference: "",
    receiptFile: null,
  };
};

/**
 * YOSHI-10935: why a SPEI row cannot be submitted yet, or null when it is complete. The API repeats
 * these checks; doing them here keeps the confirm button honest.
 */
const getSpeiRowError = (row: PaymentRow): string | null => {
  if (row.trackingKey.trim() && !SPEI_TRACKING_KEY_REGEX.test(row.trackingKey.trim())) {
    return "La clave de rastreo debe tener de 1 a 30 letras o números, sin espacios ni símbolos.";
  }
  if (row.operationAt && new Date(row.operationAt).getTime() > Date.now()) {
    return "La fecha de la transferencia no puede ser futura.";
  }
  const isTextTooLong: boolean = [row.originBank, row.senderName, row.reference].some(
    (value) => value.trim().length > SPEI_TEXT_MAX_LENGTH,
  );
  if (isTextTooLong) {
    return `El banco origen, el ordenante y la referencia deben tener máximo ${SPEI_TEXT_MAX_LENGTH} caracteres.`;
  }
  return null;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

const formatMoney = (n: number): string => {
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
};

export const CompleteSaleModal: React.FC<CompleteSaleModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  sale,
  allowSpei,
}) => {
  const paymentTypeOptions = React.useMemo(() => {
    return PAYMENT_TYPE_OPTIONS.filter((option) => allowSpei || option.path !== "spei");
  }, [allowSpei]);

  const [rows, setRows] = React.useState<PaymentRow[]>([buildEmptyRow()]);

  const activePayments = React.useMemo(() => {
    return (sale?.payments ?? []).filter((p) => {
      return ACTIVE_PAYMENT_STATUSES.has(p.status);
    });
  }, [sale]);

  const existingSubtotal = React.useMemo(() => {
    return activePayments.reduce((sum, p) => sum + (p.subtotal ?? 0), 0);
  }, [activePayments]);

  const remaining = React.useMemo(() => {
    return Math.max(0, round2((sale?.subtotal ?? 0) - existingSubtotal));
  }, [sale, existingSubtotal]);

  const isFullyCovered = remaining === 0;
  const maxNewRows = Math.max(0, MAX_PAYMENTS - activePayments.length);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setRows(isFullyCovered ? [] : [{ ...buildEmptyRow(), subtotal: String(remaining) }]);
  }, [open, isFullyCovered, remaining]);

  const handleChange = (index: number, patch: Partial<PaymentRow>): void => {
    setRows((prev) => {
      return prev.map((r, i) => {
        if (i !== index) {
          return r;
        }
        const next = { ...r, ...patch };
        if (patch.paymentType && patch.paymentType !== "card") {
          next.merchant = "";
        }
        return next;
      });
    });
  };

  const handleAddRow = (): void => {
    setRows((prev) => {
      const cleared = prev.map((r) => ({ ...r, subtotal: "" }));
      return [...cleared, buildEmptyRow()];
    });
  };

  const handleRemoveRow = (index: number): void => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 1) {
        next[0] = { ...next[0], subtotal: String(remaining) };
      }
      return next;
    });
  };

  const parsedRows = rows.map((r) => {
    return {
      subtotal: parseFloat(r.subtotal),
      tip: parseFloat(r.tip || "0"),
      paymentType: r.paymentType,
      merchant: r.merchant,
      row: r,
    };
  });

  const newSubtotalSum = round2(
    parsedRows.reduce((sum, r) => sum + (Number.isFinite(r.subtotal) ? r.subtotal : 0), 0),
  );

  const sumMatches = newSubtotalSum === remaining;

  const allRowsValid = parsedRows.every((r) => {
    if (!Number.isFinite(r.subtotal) || r.subtotal <= 0) {
      return false;
    }
    if (!Number.isFinite(r.tip) || r.tip < 0) {
      return false;
    }
    if (r.paymentType === "card" && !r.merchant) {
      return false;
    }
    if (r.paymentType === "spei") {
      return !!r.row.trackingKey.trim() && !!r.row.operationAt && getSpeiRowError(r.row) === null;
    }
    return true;
  });

  const tooManyPayments = activePayments.length + rows.length > MAX_PAYMENTS;

  const canSubmit = isFullyCovered
    ? rows.length === 0
    : rows.length > 0 && allRowsValid && sumMatches && !tooManyPayments;

  const handleConfirm = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    const payments: CompleteSalePayment[] = parsedRows.map((r) => {
      return {
        subtotal: r.subtotal,
        tip: r.tip,
        paymentType: r.paymentType,
        merchant: r.paymentType === "card" ? r.merchant : undefined,
        spei: r.paymentType === "spei"
          ? {
              trackingKey: r.row.trackingKey.trim().toUpperCase(),
              operationAt: new Date(r.row.operationAt).toISOString(),
              originBank: r.row.originBank.trim(),
              senderName: r.row.senderName.trim(),
              reference: r.row.reference.trim(),
              receiptFile: r.row.receiptFile,
            }
          : undefined,
      };
    });
    await onConfirm(payments);
  };

  return (
    <WarningModal
      open={open}
      onClose={onClose}
      title="Marcar como pagada"
      messageTitle="Advertencia importante"
      message={
        <p>
          Marcar la orden{" "}
          <span className="font-bold text-black">{sale.code}</span> como pagada
          impactará los reportes y las ventas del cliente y la concesión.
        </p>
      }
      additionalContent={
        <>
          <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Subtotal de la venta</span>
              <span className="font-semibold">{formatMoney(sale.subtotal ?? 0)}</span>
            </div>
            {existingSubtotal > 0 && (
              <div className="mt-1 flex justify-between">
                <span>Pagos existentes</span>
                <span className="font-semibold">{formatMoney(existingSubtotal)}</span>
              </div>
            )}
            <div className="mt-1 flex justify-between">
              <span>Saldo restante</span>
              <span className="font-semibold">{formatMoney(remaining)}</span>
            </div>
          </div>

          {isFullyCovered ? (
            <p className="text-sm text-gray-600">
              Los pagos existentes ya cubren el total de la venta. Al confirmar, se marcará la venta como pagada.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {rows.map((row, index) => {
                const isCard = row.paymentType === "card";
                const isSpei = row.paymentType === "spei";
                const speiError = isSpei ? getSpeiRowError(row) : null;
                const paymentTypeLabel = paymentTypeOptions.find((o) => o.path === row.paymentType)?.name ?? "";
                return (
                  <div
                    key={index}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Pago {index + 1}
                      </span>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-600"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Input
                        type="number"
                        label="Monto"
                        placeholder="0"
                        value={row.subtotal}
                        onChange={(v: string) => handleChange(index, { subtotal: v })}
                        min={0}
                      />
                      <Input
                        type="number"
                        label="Propina"
                        placeholder="0"
                        value={row.tip}
                        onChange={(v: string) => handleChange(index, { tip: v })}
                        min={0}
                      />
                    </div>
                    <Select
                      label="Medio de pago"
                      items={paymentTypeOptions}
                      value={{
                        label: paymentTypeLabel,
                        value: row.paymentType,
                      }}
                      onChange={(option) => {
                        if (
                          option != null &&
                          typeof option === "object" &&
                          "value" in option
                        ) {
                          const selected = paymentTypeOptions.find(
                            (o) => o.path === (option as { value: string }).value,
                          );
                          handleChange(index, {
                            paymentType: selected?.path ?? "card",
                          });
                        }
                      }}
                      placeholder="Selecciona medio de pago"
                    />
                    {isCard && (
                      <Select
                        label="Procesador"
                        items={MERCHANT_OPTIONS}
                        value={
                          row.merchant
                            ? { label: row.merchant, value: row.merchant }
                            : null
                        }
                        onChange={(option) => {
                          if (
                            option != null &&
                            typeof option === "object" &&
                            "value" in option
                          ) {
                            handleChange(index, {
                              merchant: (option as { value: string }).value,
                            });
                          }
                        }}
                        placeholder="Selecciona procesador"
                      />
                    )}
                    {isSpei && (
                      <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Input
                            label="Clave de rastreo *"
                            placeholder="Ej. MBAN01002610140012345"
                            value={row.trackingKey}
                            onChange={(v: string) => handleChange(index, { trackingKey: v })}
                          />
                          <Input
                            type="datetime-local"
                            label="Fecha y hora de la transferencia *"
                            value={row.operationAt}
                            onChange={(v: string) => handleChange(index, { operationAt: v })}
                          />
                          <Input
                            label="Banco origen"
                            placeholder="Ej. BBVA"
                            value={row.originBank}
                            onChange={(v: string) => handleChange(index, { originBank: v })}
                          />
                          <Input
                            label="Ordenante"
                            placeholder="Nombre de quien transfirió"
                            value={row.senderName}
                            onChange={(v: string) => handleChange(index, { senderName: v })}
                          />
                        </div>
                        <Input
                          label="Referencia / concepto"
                          placeholder="Referencia usada en la transferencia"
                          value={row.reference}
                          onChange={(v: string) => handleChange(index, { reference: v })}
                        />
                        {speiError && (
                          <p className="text-xs text-red-600">{speiError}</p>
                        )}
                        <FileUploader
                          fileName="spei-receipts"
                          acceptedTypes={["image/*", "application/pdf"]}
                          maxSize={SPEI_RECEIPT_MAX_SIZE}
                          onChange={(file: string | File) => {
                            if (file instanceof File) {
                              handleChange(index, { receiptFile: file });
                            }
                          }}
                          placeholder="Subir comprobante"
                          isFullWidth={true}
                          currentFileUrl={row.receiptFile?.name}
                          icon={<FileIcon className="h-6 w-6 text-gray-400" />}
                          mode="select"
                          label="Comprobante (opcional)"
                          customText="PDF, PNG, JPG hasta 1 MB"
                        />
                        <p className="text-xs text-gray-500">
                          Registra el pago solo después de confirmar que la transferencia llegó a la cuenta. Quedará registrado que tú lo validaste.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
              {rows.length < maxNewRows && (
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2 text-sm text-gray-600 transition-colors duration-300 hover:bg-gray-50"
                  onClick={handleAddRow}
                >
                  <Plus className="h-4 w-4" />
                  Agregar pago
                </button>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Suma de los nuevos pagos</span>
                <span
                  className={`font-semibold ${sumMatches ? "text-green-600" : "text-red-600"}`}
                >
                  {formatMoney(newSubtotalSum)} / {formatMoney(remaining)}
                </span>
              </div>
              {tooManyPayments && (
                <p className="text-xs text-red-600">
                  Una venta no puede tener más de {MAX_PAYMENTS} pagos.
                </p>
              )}
            </div>
          )}
        </>
      }
      confirmLabel="Confirmar acción"
      ConfirmIcon={CheckCircle2}
      disabled={!canSubmit}
      onConfirm={handleConfirm}
      isLoading={isLoading}
    />
  );
};
